import { get } from 'svelte/store';
import { register } from 'svelte-i18n';
import { app, os } from 'src/stores/session/native-bridge';

type LanguageAssetInfo = {
	assetPath: string;
	md5: string;
};

/**
 * Manifest cache to avoid fetching it multiple times
 */
let manifestCache: Record<string, string> | null = null;
let manifestFetchPromise: Promise<Record<string, string>> | null = null;

/**
 * Fetches and parses the manifest_app.json file
 * @returns Promise with the manifest object mapping file paths to hashes
 */
async function getManifest(): Promise<Record<string, string>> {
	if (manifestCache) {
		return manifestCache;
	}

	if (manifestFetchPromise) {
		return manifestFetchPromise;
	}

	manifestFetchPromise = (async () => {
		const host = import.meta.env.VITE_APP_BUCKET || window.location.host;
		const manifestUrl = `https://${host}/manifest_app.json`;

		try {
			const response = await fetch(manifestUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch manifest: ${response.status}`);
			}
			const manifest = await response.json();
			manifestCache = manifest;
			console.log(`[Manifest] Successfully loaded manifest from: ${manifestUrl}`);
			return manifest;
		} catch (error) {
			console.error(`Error fetching manifest_app.json from ${manifestUrl}:`, error);
			manifestFetchPromise = null;
			throw error;
		}
	})();

	return manifestFetchPromise;
}

/**
 * Finds the asset file path and md5 hash for a given language code in the manifest
 * @param langCode - Language code (e.g., 'en', 'de', 'ja-JP')
 * @param manifest - The manifest object, mapping asset paths to md5 hashes
 * @returns The asset info ({ assetPath, md5 }) or null if not found
 */
function findLanguageAsset(
	langCode: string,
	manifest: Record<string, string>,
): LanguageAssetInfo | null {
	const prefix = `assets/${langCode}-`;

	const matchingKeys = Object.keys(manifest).filter((key) => {
		if (!key.startsWith(prefix)) return false;

		const remainder = key.slice(prefix.length);
		return !remainder.includes('-');
	});

	if (matchingKeys.length === 0) {
		return null;
	}

	const assetPath = matchingKeys[0];
	const md5 = manifest[assetPath];

	if (!md5) {
		return null;
	}

	return { assetPath, md5 };
}

/**
 * Builds the full URL for an asset file
 * @param assetPath - The asset path from manifest (e.g., 'assets/en-312f84b7.js')
 * @returns The full URL to the asset
 */
function buildAssetUrl(assetPath: string): string {
	const host = import.meta.env.VITE_APP_BUCKET || window.location.host;
	return `https://${host}/${assetPath}`;
}

/**
 * Gets the language asset URL and md5 hash from the manifest for a given language code
 * @param langCode - Language code (e.g., 'en', 'de', 'ja-JP')
 * @returns Promise resolving to { url, md5 } or null if not found
 */
export async function getLanguageAssetInfo(
	langCode: string,
): Promise<{ url: string; md5: string } | null> {
	try {
		const manifest = await getManifest();
		const assetInfo: LanguageAssetInfo | null = findLanguageAsset(langCode, manifest);

		if (!assetInfo) {
			return null;
		}

		return {
			url: buildAssetUrl(assetInfo.assetPath),
			md5: assetInfo.md5,
		};
	} catch (error) {
		console.error(`Error getting language asset info for ${langCode}:`, error);
		return null;
	}
}

/**
 * Registers a language module directly with svelte-i18n using a blob URL
 * The blob is already a minified JS module (same format as generated files in dist/assets)
 * We can import it directly without extracting and re-bundling
 * @param langCode - Language code to register
 * @param blobUrl - The blob URL pointing to the JS module
 */
function registerLanguageModuleFromBlobUrl(langCode: string, blobUrl: string): void {
	register(langCode, async () => {
		try {
			const module = await import(/* @vite-ignore */ blobUrl);
			URL.revokeObjectURL(blobUrl);

			if (module.default) {
				return module.default;
			} else {
				return module;
			}
		} catch (error) {
			console.error(`Error importing language module for ${langCode}:`, error);
			throw error;
		}
	});

	console.log('successfully registered language module', langCode);
}

/**
 * Loads a language asset from native app and registers it with i18n
 * @param langCode - Language code (e.g., 'en', 'de', 'ja-JP')
 * @param assetUrl - The URL of the asset to load
 * @param assetMd5 - The md5 hash of the asset (used by native for validation)
 * @returns Promise that resolves when the asset is loaded and registered
 */
async function loadAndRegisterLanguageFromNative(
	langCode: string,
	assetUrl: string,
	assetMd5: string,
): Promise<void> {
	const currentOs = get(os);

	let result: Blob | undefined;
	if (currentOs === 'IOS') {
		const { load_asset_ios } = await import('src/services/native_container_requests_ios');
		result = await load_asset_ios(assetUrl, assetMd5);
	} else if (currentOs && currentOs !== '') {
		const { load_asset_android } = await import('src/services/native_container_requests_android');
		result = await load_asset_android(assetUrl, assetMd5);
	} else {
		throw new Error('Not in native app environment');
	}

	if (!(result instanceof Blob)) {
		throw new Error('Invalid response from native app: expected Blob');
	}
	const blob = result;

	console.log('successfully loaded blob', blob);

	if (!blob || blob.size === 0) {
		throw new Error('Empty or invalid blob received from native app');
	}

	const blobUrl = URL.createObjectURL(blob);

	console.log('successfully created blob url', blobUrl);

	registerLanguageModuleFromBlobUrl(langCode, blobUrl);
}

/**
 * Loads a language asset for the native app
 * This function reads the manifest, finds the language file, requests the native app to load it,
 * and registers it with svelte-i18n
 * @param langCode - Language code (e.g., 'en', 'de', 'ja-JP')
 * @returns Promise that resolves when the asset is loaded and registered
 */
export async function loadLanguageAsset(langCode: string): Promise<void> {
	const isNativeApp = get(app);
	if (!isNativeApp) {
		console.log(`Skipping native asset loading for ${langCode} (not in native app)`);
		return;
	}

	// Get asset URL and md5 from manifest (this will try to fetch manifest if not cached)
	const assetInfo = await getLanguageAssetInfo(langCode);

	if (!assetInfo) {
		console.warn(`Could not resolve asset URL for language: ${langCode}. Using standard import.`);
		throw new Error(`Could not resolve asset URL for language: ${langCode}`);
	}

	try {
		await loadAndRegisterLanguageFromNative(langCode, assetInfo.url, assetInfo.md5);
		console.log(`Successfully loaded language ${langCode} from native app`);
	} catch (error) {
		console.error(`Error loading language asset for ${langCode}:`, error);
		console.warn(`Falling back to standard import for ${langCode}`);
		throw new Error(`Could not load language asset for ${langCode}`);
	}
}
